/* eslint-disable react/prop-types */
import { useEffect, useState, useMemo } from "react";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import useApi from "components/Utils/api";
import toast from "react-hot-toast";
import Papa from "papaparse";
import { DateRangePicker } from "rsuite";
import { afterToday } from "rsuite/cjs/DateRangePicker/disabledDateUtils";
import reportsLineChartData from "layouts/dashboard/data/reportsLineChartData";

// ── helpers ───────────────────────────────────────────────────────────────────

const formatDateTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return isNaN(d) ? value : d.toLocaleString();
};

const STATUS_CODE_MAP = {
  S: "Success",
  F: "Failed",
  V: "Voided",
  R: "Refunded",
};

const resolveStatusLabel = (code) => STATUS_CODE_MAP[code] ?? "Failed";

const STATUS_STYLES = {
  Success: { bg: "#d4edda", color: "#155724" },
  Failed: { bg: "#f8d7da", color: "#721c24" },
  Voided: { bg: "#e2e3e5", color: "#383d41" },
  Refunded: { bg: "#fff3cd", color: "#856404" },
};

function StatusBadge({ value }) {
  const label = value;
  const s = STATUS_STYLES[label] ?? STATUS_STYLES.Failed;
  return (
    <MDBox
      sx={{
        display: "inline-block",
        px: 1.5,
        py: 0.4,
        borderRadius: "8px",
        backgroundColor: s.bg,
        color: s.color,
        fontWeight: "600",
        fontSize: "0.72rem",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </MDBox>
  );
}

const columns = [
  { Header: "MID", accessor: "mid", width: "7%", align: "left" },
  { Header: "TID", accessor: "tid", width: "6%", align: "left" },
  { Header: "POS ID", accessor: "posId", width: "7%", align: "left" },
  {
    Header: "Terminal Txn Date",
    accessor: "terminalTransactionDate",
    width: "10%",
    align: "left",
    Cell: ({ value }) => <span>{formatDateTime(value)}</span>,
  },
  {
    Header: "Amount",
    accessor: (row) => (row.amount != null ? `${row.amount.toFixed(2)} ${row.currency ?? ""}`.trim() : "—"),
    width: "7%",
    align: "left",
  },
  {
    Header: "Status",
    accessor: (row) => resolveStatusLabel(row.status),
    width: "7%",
    align: "left",
    Cell: ({ value }) => <StatusBadge value={value} />,
  },
  { Header: "PAN", accessor: "maskedPan", width: "8%", align: "left" },
  { Header: "Brand", accessor: "cardBrand", width: "6%", align: "left" },
  { Header: "Transaction Number", accessor: "transactionNumber", width: "9%", align: "left" },
  { Header: "RRN", accessor: "rrn", width: "6%", align: "left" },
  { Header: "Username", accessor: "userName", width: "7%", align: "left" },
  { Header: "Device ID", accessor: "deviceId", width: "8%", align: "left" },
  { Header: "Serial Number", accessor: "serialNumber", width: "8%", align: "left" },
  {
    Header: "Txn Date",
    accessor: "transactionDate",
    width: "9%",
    align: "left",
    Cell: ({ value }) => <span>{formatDateTime(value)}</span>,
  },
  { Header: "Auth Code", accessor: "authCode", width: "6%", align: "left" },
  { Header: "Order No", accessor: "merchantOrderNo", width: "7%", align: "left" },
  { Header: "Response Code", accessor: "responseCode", width: "6%", align: "left" },
  { Header: "Settlement Status", accessor: "settlementStatus", width: "7%", align: "left" },
  { Header: "Pay Method", accessor: "payMethod", width: "6%", align: "left" },
  {
    Header: "Comments",
    accessor: "comments",
    width: "10%",
    align: "left",
    Cell: ({ value }) => (
      <span title={value} style={{ display: "inline-block", maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {value ?? "—"}
      </span>
    ),
  },
];

// ── main page ─────────────────────────────────────────────────────────────────

function PosTransactions({ embedded = false, tabsSlot = null } = {}) {
  const { predefinedRanges } = reportsLineChartData;
  const [rows, setRows] = useState([]);
  const [filterSearch, setFilterSearch] = useState("");
  const [lastMonthDateRange, setLastMonthDateRange] = useState([
    new Date().toISOString().slice(0, 10),
    new Date().toISOString().slice(0, 10),
  ]);

  const api = useApi();

  const formatDate = (date) => new Date(date).toISOString().slice(0, 10);

  const filteredRows = useMemo(() => {
    const search = filterSearch.trim().toLowerCase();
    if (!search) return rows;
    return rows.filter((r) => {
      const searchableValues = [
        r.transactionNumber,
        r.rrn,
        r.tid,
        r.mid,
        r.posId,
        r.userName,
        r.deviceId,
        r.merchantOrderNo,
        r.authCode,
        r.status,
        resolveStatusLabel(r.status),
        r.settlementStatus,
        r.maskedPan,
      ];
      return searchableValues.some((v) => v != null && String(v).toLowerCase().includes(search));
    });
  }, [rows, filterSearch]);

  const fetchData = (range) => {
    if (range == null) return;
    setLastMonthDateRange(range);
    api
      .get("pos/transactions", {
        params: {
          fromDate: formatDate(range[0]),
          toDate: formatDate(range[1]),
        },
      })
      .then((res) => {
        setRows(Array.isArray(res.data) ? res.data : []);
      })
      .catch((err) => {
        if (err.response?.status === 403) {
          toast.error("You do not have permission to perform this action.");
          return;
        }
        setRows([]);
      });
  };

  const handleDownloadCSV = () => {
    if (filteredRows.length === 0) {
      toast.error("No records found to download");
      return;
    }
    const headers = columns.map((c) => c.Header);
    const values = filteredRows.map((row) =>
      columns.map((c) => (typeof c.accessor === "function" ? c.accessor(row) : row[c.accessor]))
    );
    const csv = Papa.unparse([headers, ...values], { quotes: true });
    const ts = new Date().toISOString().slice(0, 19).replace(/:/g, "-");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `PosTransactions_${ts}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  useEffect(() => {
    fetchData(lastMonthDateRange);
  }, []); // eslint-disable-line

  const content = (
    <MDBox mt={3} mb={3}>
      <Grid container spacing={3} justifyContent="center">
        <Grid item xs={12}>
          <Grid container alignItems="center">
            <Grid item xs={4}>
              <MDBox>
                <MDTypography variant="h5" fontWeight="medium">
                  Reports
                </MDTypography>
                <MDTypography variant="button" fontWeight="regular" color="secondary">
                  {filteredRows.length} POS Transactions
                </MDTypography>
              </MDBox>
            </Grid>
            <Grid item xs={4} display="flex" justifyContent="center">
              <DateRangePicker
                disabledDate={afterToday()}
                ranges={predefinedRanges}
                value={Object.keys(lastMonthDateRange).length !== 0 ? [new Date(lastMonthDateRange[0]), new Date(lastMonthDateRange[1])] : []}
                onChange={(range) => fetchData(range)}
                format="d MMMM yyyy"
              />
            </Grid>
            <Grid item xs={4} display="flex" justifyContent="flex-end">
              <MDButton variant="gradient" color="info" onClick={handleDownloadCSV}>
                Export CSV
              </MDButton>
            </Grid>
          </Grid>
        </Grid>
        {tabsSlot && (
          <Grid item xs={12}>
            {tabsSlot}
          </Grid>
        )}
        <Grid item xs={12} lg={12}>
          <MDBox sx={(theme) => ({ border: `1px solid ${theme.palette.grey[300]}`, borderRadius: "8px", background: theme.palette.white.main, overflow: "hidden" })}>
            <MDBox px={2} py={1.5} sx={(theme) => ({ borderBottom: `1px solid ${theme.palette.grey[300]}`, background: theme.palette.background.hover })}>
              <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <TextField
                  label="Search"
                  placeholder="Transaction Number, RRN, TID, MID, POS ID, Username, Status..."
                  size="small"
                  value={filterSearch}
                  onChange={(e) => setFilterSearch(e.target.value)}
                  sx={{ minWidth: 260, "& .MuiOutlinedInput-root": { height: "36.5px" } }}
                />
              </MDBox>
            </MDBox>

            <DataTable
              table={{ columns, rows: filteredRows }}
              showTotalEntries
              isSorted
              noEndBorder
              hideExpandColumn
              entriesPerPage
              entriesPerPagePosition="bottom"
              canSearch={false}
            />
          </MDBox>
        </Grid>
      </Grid>
    </MDBox>
  );

  if (embedded) return content;

  return (
    <DashboardLayout>
      {content}
      <Footer />
    </DashboardLayout>
  );
}

export default PosTransactions;
